/**
 * Flight Edit Page
 * Edit existing flight log entries (legacy system)
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';
import { useFlight, useUpdateFlight, useDeleteFlight } from '@/hooks/useFlights';
import { useAuth } from '@/contexts/AuthContext';
import FlightEntryForm from '@/components/FlightEntryForm';
import Button from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardBody } from '@/components/ui/Card';
import { ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { CreateFlightLogRequest } from '@/types/flight';

export default function FlightEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  const flightId = parseInt(id || '0');
  const { data: flight, isLoading } = useFlight(flightId);
  const updateFlightMutation = useUpdateFlight();
  const deleteFlightMutation = useDeleteFlight();

  const handleUpdate = async (data: CreateFlightLogRequest) => {
    try {
      await updateFlightMutation.mutateAsync({
        id: flightId,
        data,
      });

      showSuccess('Flight Updated', 'Flight entry has been updated successfully');
      navigate('/flights');
    } catch (error: any) {
      showError(
        'Update Failed',
        error?.response?.data?.error || error?.message || 'Please try again'
      );
      throw error; // Re-throw so form can handle it
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this flight? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteFlightMutation.mutateAsync(flightId);
      showSuccess('Flight Deleted', 'Flight entry has been deleted successfully');
      navigate('/flights');
    } catch (error: any) {
      showError(
        'Delete Failed',
        error?.response?.data?.error || error?.message || 'Please try again'
      );
    }
  };

  const handleCancel = () => {
    navigate('/flights');
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-96 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!flight) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-foreground mb-4">Flight Not Found</h1>
          <p className="text-foreground/60 mb-6">The flight you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/flights')}>
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Flights
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate('/flights')}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              aria-label="Back to flights"
            >
              <ArrowLeftIcon className="w-5 h-5" style={{ color: 'var(--text-primary)' }} />
            </button>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Edit Flight
            </h1>
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>
            Flight on {new Date(flight.flightDate).toLocaleDateString()} • {flight.tailNumber}
          </p>
        </div>

        {/* Delete Button */}
        <Button
          variant="outline"
          onClick={handleDelete}
          disabled={deleteFlightMutation.isPending}
          isLoading={deleteFlightMutation.isPending}
        >
          <TrashIcon className="w-5 h-5 mr-2" />
          Delete
        </Button>
      </div>

      {/* Edit Form */}
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>Flight Details</CardTitle>
        </CardHeader>
        <CardBody>
          <FlightEntryForm
            flight={flight}
            userId={user?.id || flight.userId}
            onSubmit={handleUpdate}
            onCancel={handleCancel}
            isLoading={updateFlightMutation.isPending}
          />
        </CardBody>
      </Card>
    </div>
  );
}
